
import Page from '@/Page.vue';
import { Component, Prop, VModel, Watch } from 'vue-property-decorator';
import {
    PermissionTypeEnum,
    ProductModel,
    ScrapReasonModel,
    ShiftModel,
    ShiftScrapRecordModel,
    ScrapReasonWorkplaceModel,
    StationModel,
} from '@/libs/Api';
import { scrapReasonsStore, scrapReasonWorkplacesStore } from '@/libs/scrap-reasons/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { shiftScrapRecordsStore } from '@/libs/shifts/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import moment from 'moment';
import { stationsStore } from '@/libs/stations/+state/store';

@Component({})
export default class ShiftScrapRecordDialog extends Page {
    @VModel() dialog!: boolean;
    @Prop() shift!: ShiftModel;
    @Prop() stationId?: string;
    @Prop() activeRecordId?: string;

    protected recordTime: string | null = null;
    protected activeStation: StationModel | null = null;
    protected activeProduct: ProductModel | null = null;
    protected activeScrapReason: ScrapReasonModel | null = null;
    protected scrapCount: number | null = null;
    protected isWaitingToSave = false;
    protected savingTimeout: any = null;
    protected customScrapCount = false;

    private minScrapCountHint = 1;
    private maxScrapCountHint = 10;

    @Watch('dialog')
    async dialogChanged(v: boolean) {
        if (v) {
            this.activeStation = null;
            this.activeProduct = null;
            this.activeScrapReason = null;
            this.isWaitingToSave = false;
            this.savingTimeout = null;
            this.customScrapCount = false;
            this.scrapCount = null;
            if (!this.activeRecordId) {
                this.makePresets();
            } else {
                shiftScrapRecordsStore.dispatch(CrudAction.Get, { id: this.activeRecordId, silent: true });
                const record = await this.subscribe(shiftScrapRecordsStore, CrudReponse.Get);
                this.setRecord(record);
            }
        }
    }

    get products(): ProductModel[] {
        const shiftScrapRecord: ShiftScrapRecordModel | null = shiftScrapRecordsStore.useGetter(CrudGetter.Detail);
        const items = this.shift?.shiftProductions?.filter((x) => x.product).map((x) => x.product!) ?? [];
        if (shiftScrapRecord && shiftScrapRecord!.productId && !items.some((x) => x.id == shiftScrapRecord!.productId)) {
            items.push(shiftScrapRecord.product!);
        }
        return items.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    get scrapReasons(): ScrapReasonModel[] {
        const shiftScrapRecord: ShiftScrapRecordModel | null = shiftScrapRecordsStore.useGetter(CrudGetter.Detail);
        const scrapReasonWorkplaces: ScrapReasonWorkplaceModel[] = scrapReasonWorkplacesStore.useGetter(CrudGetter.Data) ?? [];
        const activeStation = this.activeStation;
        let items: ScrapReasonModel[] = scrapReasonsStore.useGetter(CrudGetter.Data) ?? [];
        if (
            shiftScrapRecord &&
            shiftScrapRecord!.scrapReasonId &&
            !items.some((x) => x.id == shiftScrapRecord!.scrapReasonId)
        ) {
            items.push(shiftScrapRecord.scrapReason!);
        }
        items = items.filter((x) => {
            if (!x.useWorkplaceLimitation) {
                return true;
            } else {
                const scrapReasonWorkplace = scrapReasonWorkplaces.find(
                    (t) => t.scrapReasonId == x.id && t.workplaceId == this.shift.workplaceId
                );
                if (scrapReasonWorkplace) {
                    if (!scrapReasonWorkplace.useStationLimitation) {
                        return true;
                    } else {
                        if (activeStation?.id) {
                            return (scrapReasonWorkplace.scrapReasonWorkplaceStations ?? []).some(
                                (n) => n.stationId == activeStation!.id
                            );
                        } else {
                            return false;
                        }
                    }
                } else {
                    return false;
                }
            }
        });
        return items.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    get stations(): StationModel[] {
        const shiftScrapRecord: ShiftScrapRecordModel | null = shiftScrapRecordsStore.useGetter(CrudGetter.Detail);
        const items: StationModel[] = ((stationsStore.useGetter(CrudGetter.Data) ?? []) as StationModel[]).filter(
            (x) => x.workplaceId == this.shift.workplaceId
        );
        if (shiftScrapRecord && shiftScrapRecord!.stationId && !items.some((x) => x.id == shiftScrapRecord!.stationId)) {
            items.push(shiftScrapRecord.station!);
        }
        return items.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }

    get isSaving(): boolean {
        return shiftScrapRecordsStore.useGetter(CrudGetter.Saving);
    }

    get isBadRecordTime(): boolean {
        return (
            moment(this.recordTime).isBefore(moment(this.shift.fromDate)) ||
            moment(this.recordTime).isAfter(moment(this.shift.toDate))
        );
    }

    get scrapCountHints(): (number | null)[] {
        const items: (number | null)[] = [];
        for (let i = this.minScrapCountHint; i <= this.maxScrapCountHint; i++) {
            items.push(i);
        }
        items.push(null);
        return items;
    }

    async mounted() {
        this.$store.subscribe((mutation, state) => {
            if (mutation.type === shiftScrapRecordsStore.getActionName(CrudAction.Update)) {
                if (mutation.payload.item.id == this.activeRecordId) {
                    const isSaving = shiftScrapRecordsStore.useGetter(CrudGetter.Saving);
                    if (!isSaving) {
                        this.setRecord(mutation.payload.item);
                    }
                }
            }
        });
        if (this.dialog) {
            if (!this.activeRecordId) {
                this.makePresets();
            } else {
                shiftScrapRecordsStore.dispatch(CrudAction.Get, { id: this.activeRecordId });
                const record: ShiftScrapRecordModel = await this.subscribe(shiftScrapRecordsStore, CrudReponse.Get);
                this.setRecord(record);
            }
        }
    }

    setRecord(record: ShiftScrapRecordModel) {
        this.activeStation = record.station ?? null;
        this.activeProduct = record.product ?? null;
        this.activeScrapReason = record.scrapReason ?? null;
        this.customScrapCount =
            !record.scrapCount || record.scrapCount < this.minScrapCountHint || record.scrapCount > this.maxScrapCountHint;
        this.scrapCount = record.scrapCount ?? null;
        this.recordTime = record.recordTime ?? null;
    }

    makePresets() {
        const products: ProductModel[] = this.shift?.shiftProductions?.filter((x) => x.product).map((x) => x.product!) ?? [];
        if (products.length == 1) {
            this.activeProduct = products[0];
        }
        let stations: StationModel[] = ((stationsStore.useGetter(CrudGetter.Data) ?? []) as StationModel[]).filter(
            (x) => x.workplaceId == this.shift?.workplaceId
        );
        if (this.stationId) {
            stations = stations.filter((x) => x.id == this.stationId);
        }
        if (stations.length == 1 && this.activeStation == null) {
            this.activeStation = stations[0];
        }
        if (moment().isAfter(this.shift.toDate)) {
            this.recordTime = moment(this.shift.toDate).subtract(1, 'minutes').format('YYYY-MM-DDTHH:mm:ss');
        } else {
            this.recordTime = moment().format('YYYY-MM-DDTHH:mm:ss');
        }
    }

    onCloseClick() {
        this.$emit('input', false);
        if (this.isWaitingToSave) {
            this.onCancelSaveClick();
        }
    }

    setActiveProduct(product: ProductModel | null) {
        this.activeProduct = product;
    }

    setActiveStation(station: StationModel | null) {
        this.activeStation = station;
        this.$forceUpdate();
    }

    setActiveScrapReason(scrapReason: ScrapReasonModel | null) {
        this.activeScrapReason = scrapReason;
    }

    isLimitedWork(): boolean {
        return (
            this.hasPermissions([PermissionTypeEnum.ShiftWorkLimited]) && !this.hasPermissions([PermissionTypeEnum.ShiftWork])
        );
    }

    async onSaveClick() {
        if (this.isLimitedWork()) {
            this.isWaitingToSave = true;
        }
        this.savingTimeout = setTimeout(
            async () => {
                try {
                    if (!this.activeRecordId) {
                        shiftScrapRecordsStore.dispatch(CrudAction.Create, {
                            item: {
                                shiftId: this.shift.id,
                                stationId: this.stationId ?? this.activeStation?.id,
                                productId: this.activeProduct!.id,
                                scrapReasonId: this.activeScrapReason!.id,
                                recordTime: this.recordTime,
                                scrapCount: this.scrapCount,
                            } as ShiftScrapRecordModel,
                        });
                        await this.subscribe(shiftScrapRecordsStore, CrudReponse.Create);
                    } else {
                        const record: ShiftScrapRecordModel = shiftScrapRecordsStore.useGetter(CrudGetter.Detail);
                        record.stationId = this.stationId ?? this.activeStation?.id;
                        record.productId = this.activeProduct!.id;
                        record.scrapReasonId = this.activeScrapReason!.id;
                        record.scrapCount = this.scrapCount!;
                        record.recordTime = this.recordTime!;
                        shiftScrapRecordsStore.dispatch(CrudAction.Update, {
                            item: record,
                        });
                        await this.subscribe(shiftScrapRecordsStore, CrudReponse.Update);
                    }
                    this.isWaitingToSave = false;
                    this.onCloseClick();
                } catch (e) {
                    console.error(e);
                    this.isWaitingToSave = false;
                }
            },
            this.isLimitedWork() ? 3000 : 0
        );
    }

    onCancelSaveClick() {
        clearTimeout(this.savingTimeout);
        this.isWaitingToSave = false;
    }

    setScrapCount(count: number | null) {
        if (count == null) {
            if (!this.customScrapCount) {
                this.customScrapCount = true;
                this.scrapCount = null;
            }
        } else {
            this.scrapCount = count;
        }
    }
}
